/**
 * AdminPropertyStatePanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import connect from "react-redux/es/connect/connect";

// Material UI
import { withStyles } from '@material-ui/core/styles';

// Material UI components
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from "@material-ui/core/Tooltip";

// Material UI icons
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';

// application components
import WarningPanel from "../base/WarningPanel";

// application
import {fetchJSON} from "../../operations/communication";
import {
    REST_CALL_UPDATE_SERVER_PROPERTY_GLOBALS,
    REST_CALL_UPDATE_SERVER_PROPERTY_LIST,
    REST_CALL_UPDATE_SERVER_PROPERTY_VALUES,
    REST_CALL_DELETE_SERVER_PROPERTY_VALUE,
    REST_SERVER_BASE_URL
} from "../../config";
import {toUserFriendlySecondsString} from "../../utils";


/**
 * Panel for admin page to show property values fetched from server
 *
 * @author Jens Gruschel
 */
class AdminPropertyStatePanel extends React.Component {

    state = {
        disconnected: false,
        expandedPropertyId: "globals",
        serverTime: undefined,
        globalProperties: [],
        visitorProperties: [],
        values: {}
    };

    componentDidMount() {
        this.refreshData();
        this.timer = setInterval(() => {this.refreshData()}, 5000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    refreshData() {
        this.refreshPropertyList();
        if (this.state.expandedPropertyId === "globals") {
            this.refreshPropertyGlobals(this.state.expandedPropertyId);
        }
        else if (this.state.expandedPropertyId) {
            this.refreshPropertyValues(this.state.expandedPropertyId);
        }
    }

    async refreshPropertyList() {

        const result = await this.fetchServerPropertyList();

        if (result) {
            this.setState({
                disconnected: false,
                visitorProperties: result.visitorproperties.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
            });
        }
        else {
            this.setState({
                disconnected: true
            });
        }
    }

    async refreshPropertyGlobals() {

        const result = await this.fetchServerPropertyGlobals();

        if (result) {
            this.setState({
                disconnected: false,
                serverTime: result.time,
                globalProperties: result.globalproperties.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
            });
        }
        else {
            this.setState({
                disconnected: true
            });
        }
    }


    async refreshPropertyValues(propertyId) {

        const result = await this.fetchServerPropertyValues(propertyId);

        if (result) {
            const values = result.values || [];
            this.setState({
                disconnected: false,
                serverTime: result.time,
                values: {
                    ...this.state.values,
                    [propertyId]: values.sort((a, b) => a.individual.localeCompare(b.individual))
                }
            });
        }
        else {
            this.setState({
                disconnected: true
            });
        }
    }

    handlePropertyChange(id) {
        this.setState({
            expandedPropertyId: id
        });
        this.refreshPropertyValues(id);
    }

    render() {

        const { classes } = this.props;

        return <div className={classes.accordion}>

            {
                this.state.disconnected && <WarningPanel text="Verbindung zum Server unterbrochen!" />
            }

            <ExpansionPanel
                key="globals"
                expanded={this.state.expandedPropertyId === "globals"}
                onChange={(event, isExpanded) => this.handlePropertyChange(isExpanded ? "globals" : null)}
            >

                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography className={classes.heading}>globale Merkmale</Typography>
                    <Typography className={classes.secondaryHeading}></Typography>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Wert</TableCell>
                                <TableCell align="right">letzte Änderung</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.state.globalProperties.map(property => {
                                return <TableRow key={property.id}>
                                    <TableCell>
                                        <Tooltip title={property.remarks || property.id}><span>{property.name}</span></Tooltip>
                                    </TableCell>
                                    <TableCell align="right">{property.value}</TableCell>
                                    <TableCell align="right">{toUserFriendlySecondsString(Math.floor(this.state.serverTime - property.time))}</TableCell>
                                    <TableCell className={classes.rowActionCell} align="right">
                                        <Tooltip title="Löschen">
                                            <IconButton
                                                onClick={() => this.deleteServerPropertyValue(property.id, "global")}
                                            >
                                                <DeleteForeverIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>;
                            })}
                        </TableBody>
                    </Table>

                </ExpansionPanelDetails>

            </ExpansionPanel>

            {this.state.visitorProperties.map(
                property => <ExpansionPanel
                    key={property.id}
                    expanded={this.state.expandedPropertyId === property.id}
                    onChange={(event, isExpanded) => this.handlePropertyChange(isExpanded ? property.id : null)}
                >

                    <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography className={classes.heading}>{property.name}</Typography>
                        <Typography className={classes.secondaryHeading}>{property.remarks}</Typography>
                    </ExpansionPanelSummary>

                    <ExpansionPanelDetails>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Individuum</TableCell>
                                    <TableCell align="right">Wert</TableCell>
                                    <TableCell align="right">letzte Änderung</TableCell>
                                    <TableCell align="right">Aktionen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {this.state.values[property.id] && this.state.values[property.id].map(value => {
                                    return <TableRow key={value.individual}>
                                        <TableCell>{value.name || value.individual}</TableCell>
                                        <TableCell align="right">{value.value}</TableCell>
                                        <TableCell align="right">{toUserFriendlySecondsString(Math.floor(this.state.serverTime - value.time))}</TableCell>
                                        <TableCell className={classes.rowActionCell} align="right">
                                            <Tooltip title="Löschen">
                                                <IconButton
                                                    onClick={() => this.deleteServerPropertyValue(property.id, value.individual)}
                                                >
                                                    <DeleteForeverIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>;
                                })}
                            </TableBody>
                        </Table>

                    </ExpansionPanelDetails>

                </ExpansionPanel>
            )}
        </div>;
    }

    async fetchServerPropertyGlobals() {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_PROPERTY_GLOBALS.path,
                {
                    method: REST_CALL_UPDATE_SERVER_PROPERTY_GLOBALS.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return result;
        }
        catch (error) {
            return null;
        }
    }
    
    async fetchServerPropertyList() {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_PROPERTY_LIST.path,
                {
                    method: REST_CALL_UPDATE_SERVER_PROPERTY_LIST.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return result;
        }
        catch (error) {
            return null;
        }
    }
    
    async fetchServerPropertyValues(propertyId) {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_UPDATE_SERVER_PROPERTY_VALUES.path.replace(
                    "<id>", propertyId
                ),
                {
                    method: REST_CALL_UPDATE_SERVER_PROPERTY_VALUES.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            return result;
        }
        catch (error) {
            return null;
        }
    }

    async deleteServerPropertyValue(propertyId, individual) {
        try {
            const result = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_DELETE_SERVER_PROPERTY_VALUE.path.replace(
                    "<id>", propertyId
                ).replace(
                    "<individual>", individual
                ),
                {
                    method: REST_CALL_DELETE_SERVER_PROPERTY_VALUE.method,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            console.log(result.message);
            this.refreshData();
            return true;
        }
        catch (error) {
            return false;
        }
    }
}

const styles = theme => ({
    accordion: {
        width: '100%'
    },
    heading: {
        fontSize: theme.typography.pxToRem(15),
        flexBasis: '33.33%',
        flexShrink: 0,
    },
    secondaryHeading: {
        fontSize: theme.typography.pxToRem(15),
        color: theme.palette.text.secondary,
    }
});

const mapStateToProps = state => {
    return {
        accessToken: state.authorization.accessToken
    };
};

export default connect(mapStateToProps)(withStyles(styles)(AdminPropertyStatePanel));